'use client'

import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { IconChevDown, IconChevUp, IconSearch } from '@/app/components/atoms/Icons'
import { cx } from '@/app/lib/utils'
import { FieldMessage, useFieldIds } from '@/app/components/atoms/forms/field'
import styles from './MultiSelectField.module.scss'

/** Insensible a mayúsculas y acentos (p. ej. "Coruña" ~ "coruna"), para buscar en las opciones. */
function normalizeForSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

export interface SelectOption {
  label: string
  value: string
}

export interface MultiSelectFieldProps {
  label: string
  values: string[]
  options: SelectOption[]
  onChange: (values: string[]) => void
  disabled?: boolean
  required?: boolean
  errorText?: string
  id?: string
  /** Valores que actúan como sentinel (select-all): se excluyen del conteo y del label mostrado. */
  sentinelValues?: string[]
}

/**
 * Campo de selección múltiple del DS. Patrón accesible disclosure + grupo de
 * checkboxes nativos: el desplegable contiene `<input type="checkbox">` reales,
 * navegables por teclado (Tab/Espacio) y anunciados por lectores de pantalla.
 * Cierra con Escape, click fuera o al salir el foco.
 */
export function MultiSelectField({
  label,
  values,
  options,
  onChange,
  disabled = false,
  required,
  errorText,
  id,
  sentinelValues,
}: MultiSelectFieldProps) {
  const { fieldId, messageId } = useFieldIds(id)
  const listId = `${fieldId}-list`
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')

  const hasError = Boolean(errorText)
  const isFilled = values.length > 0
  const realValues = sentinelValues ? values.filter((v) => !sentinelValues.includes(v)) : values
  const displayLabel =
    realValues.length === 0
      ? ''
      : realValues.length === 1
        ? (options.find((o) => o.value === realValues[0])?.label ?? realValues[0])
        : `${realValues.length} seleccionados`

  const filteredOptions = useMemo(() => {
    if (query === '') return options
    const normalizedQuery = normalizeForSearch(query)
    return options.filter((option) => normalizeForSearch(option.label).includes(normalizedQuery))
  }, [options, query])

  // Foco del buscador al abrir: efecto puramente de DOM (el input todavía no
  // existe en el render donde se pulsa el disparador), sin tocar estado.
  useEffect(() => {
    if (isOpen) searchInputRef.current?.focus()
  }, [isOpen])

  // Cierra al hacer click fuera o al mover el foco fuera del contenedor.
  useEffect(() => {
    if (!isOpen) return
    const container = containerRef.current

    const handleOutside = (e: MouseEvent) => {
      if (!container?.contains(e.target as Node)) closeDropdown(false)
    }
    const handleFocusOut = (e: FocusEvent) => {
      if (!container?.contains(e.relatedTarget as Node)) closeDropdown(false)
    }

    document.addEventListener('mousedown', handleOutside)
    container?.addEventListener('focusout', handleFocusOut)
    return () => {
      document.removeEventListener('mousedown', handleOutside)
      container?.removeEventListener('focusout', handleFocusOut)
    }
  }, [isOpen])

  // Abre/cierra limpiando la búsqueda en el mismo gesto que causa el cambio
  // — no en un efecto reaccionando a `isOpen`, que dispararía un render extra.
  function openDropdown() {
    setQuery('')
    setIsOpen(true)
  }

  function closeDropdown(returnFocus: boolean) {
    setIsOpen(false)
    setQuery('')
    if (returnFocus) triggerRef.current?.focus()
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen) {
      closeDropdown(true)
    }
  }

  const toggleOption = (value: string) => {
    onChange(values.includes(value) ? values.filter((v) => v !== value) : [...values, value])
  }

  return (
    <div className={styles.root}>
      <div
        ref={containerRef}
        className={cx(
          styles.field,
          isOpen && styles.active,
          disabled && styles.disabled,
          hasError && styles.error,
        )}
        onKeyDown={handleKeyDown}
      >
        <button
          ref={triggerRef}
          type="button"
          className={styles.trigger}
          onClick={() => {
            if (disabled) return
            if (isOpen) closeDropdown(false)
            else openDropdown()
          }}
          aria-expanded={isOpen}
          aria-controls={isOpen ? listId : undefined}
          aria-describedby={hasError ? messageId : undefined}
          disabled={disabled}
        >
          <span className={cx(styles.label, (isFilled || isOpen) && styles.labelElevated)}>
            {label}{required && <span aria-hidden="true"> *</span>}
          </span>
          {isFilled && <span className={styles.value}>{displayLabel}</span>}
          <span className={styles.chevron} aria-hidden="true">
            {isOpen ? <IconChevUp size={14} /> : <IconChevDown size={14} />}
          </span>
        </button>

        {isOpen && (
          <div id={listId} className={styles.dropdown} role="group" aria-label={label}>
            <div className={styles.searchRow}>
              <IconSearch size={20} aria-hidden="true" />
              <input
                ref={searchInputRef}
                type="text"
                className={styles.searchInput}
                placeholder="Buscar…"
                aria-label={`Buscar en ${label}`}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            <div className={styles.optionList}>
              {filteredOptions.length === 0 ? (
                <p className={styles.noResults} role="status">
                  Sin resultados
                </p>
              ) : (
                filteredOptions.map((option) => {
                  const checked = values.includes(option.value)
                  return (
                    <label
                      key={option.value}
                      className={cx(styles.option, checked && styles.optionSelected)}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={checked}
                        onChange={() => toggleOption(option.value)}
                      />
                      <span className={styles.indicator} aria-hidden="true">
                        {checked && (
                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                            <path d="M1 4l2.5 2.5L9 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </span>
                      <span>{option.label}</span>
                    </label>
                  )
                })
              )}
            </div>

            <div className={styles.dropdownFooter}>
              <button
                type="button"
                className={styles.footerBtn}
                onClick={() => onChange([])}
                aria-label={`Borrar selección de ${label}`}
              >
                Borrar
              </button>
              <button
                type="button"
                className={cx(styles.footerBtn, styles.footerBtnAccept)}
                onClick={() => closeDropdown(true)}
                aria-label={`Aceptar selección de ${label}`}
              >
                Aceptar
              </button>
            </div>
          </div>
        )}
      </div>

      <FieldMessage id={messageId} error={errorText} />
    </div>
  )
}
